import type { ReactNode } from 'react'
import { Button } from '@/components/ui/Button'

interface ShellProps { children: ReactNode }

function AuthShell({ children }: ShellProps) {
  return (
    <div className="auth-stage" style={{ display: 'grid', placeItems: 'center', minHeight: '100vh', padding: 'var(--sp-32)' }}>
      <div className="card pad" style={{ maxWidth: 480, width: '100%' }}>
        <div className="brand" style={{ marginBottom: 'var(--sp-24)' }}>
          <img className="brand-logo" src="/console/Fusion-Logo.png" alt="Fusion II logo" />Fusion II <span className="ver">Console</span>
        </div>
        {children}
      </div>
    </div>
  )
}

export function SignInLanding({ onSignIn }: { onSignIn: () => void }) {
  return (
    <AuthShell>
      <h1>Sign in</h1>
      <p>Use your organisation account to access the console. Access is governed by your Entra ID group memberships.</p>
      <Button variant="primary" onClick={onSignIn}>Sign in with Microsoft</Button>
      <p className="muted" style={{ fontSize: 'var(--fs-200)', marginTop: 'var(--sp-16)' }}>
        Tenant <span className="mono">spiinc.net</span> · production
      </p>
    </AuthShell>
  )
}

export function OidcRedirect({ overage = false }: { overage?: boolean }) {
  return (
    <AuthShell>
      <div role="status" aria-live="polite" style={{ textAlign: 'center' }}>
        <div className="spinner" />
        <h1 style={{ marginTop: 'var(--sp-16)' }}>Completing sign-in…</h1>
        <p>Returning from Microsoft and establishing your session.</p>
      </div>
      {overage && (
        <div className="constraint-note" style={{ marginTop: 'var(--sp-16)' }}>
          <b>Resolving group memberships from Microsoft Graph…</b>
          <p className="muted" style={{ fontSize: 'var(--fs-200)' }}>
            You're a member of many groups, so Entra omitted them from the token. We're fetching them directly — no action needed.
          </p>
        </div>
      )}
    </AuthShell>
  )
}

export function NoAccess({ heldGroups, upn }: { heldGroups: string[]; upn: string }) {
  return (
    <AuthShell>
      <h1>You don't have access yet</h1>
      <p>
        You're signed in as <b className="mono">{upn}</b>, but none of your Entra groups map to a console role.
        Ask an administrator to grant access.
      </p>
      <div className="card pad" style={{ marginTop: 'var(--sp-16)' }}>
        <div className="muted" style={{ fontSize: 'var(--fs-200)' }}>Your group memberships</div>
        <div style={{ marginTop: 'var(--sp-8)' }}>
          {heldGroups.length > 0
            ? heldGroups.map((g) => <span key={g} className="chip" style={{ fontSize: 'var(--fs-200)' }}>{g}</span>)
            : <span className="muted">No groups returned by Entra.</span>}
        </div>
        <p className="muted" style={{ fontSize: 'var(--fs-100)', marginTop: 'var(--sp-12)' }}>
          An administrator will map one of your groups to grant access. We don't list which groups grant access here.
        </p>
      </div>
      <div className="row" style={{ gap: 8, marginTop: 'var(--sp-16)', justifyContent: 'center' }}>
        <a className="btn primary" href="mailto:fusion-access@spiinc.net?subject=Fusion%20II%20Console%20access">Request access by email</a>
      </div>
    </AuthShell>
  )
}
